"use client";

import type React from "react";
import { useState, useRef, useEffect } from "react";
import { EditIcon } from "@chakra-ui/icons";
import { Flex, Heading, Input, IconButton, useToast } from "@chakra-ui/react";
import { smsAgentService } from "../../sms-services/agentService";

interface EditableSMSAgentNameProps {
  agentId: string;
  initialName: string;
}

export const EditableSMSAgentName: React.FC<EditableSMSAgentNameProps> = ({
  agentId,
  initialName,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(initialName);
  const inputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (name.trim() === "") {
      setName(initialName);
      setIsEditing(false);
      return;
    }

    try {
      await smsAgentService.update(agentId, { name });
      setIsEditing(false);
      toast({
        title: "Agent name updated",
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      console.error("Error updating agent name:", error);
      toast({
        title: "Error updating agent name",
        status: "error",
        duration: 3000,
      });
      setName(initialName);
      setIsEditing(false);
    }
  };

  return (
    <Flex align="left">
      {isEditing ? (
        <Input
          ref={inputRef}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={handleSave}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleSave();
            } else if (e.key === "Escape") {
              setName(initialName);
              setIsEditing(false);
            }
          }}
          size="lg"
          width="auto"
          height="auto"
          fontWeight="medium"
          _focus={{ borderColor: "transparent" }}
          borderColor="transparent"
          variant="filled"
          outline="none"
        />
      ) : (
        <>
          <Heading size="md" fontWeight="medium">
            {name}
          </Heading>
          <IconButton
            aria-label="Edit agent name"
            icon={<EditIcon />}
            size="xs"
            variant="ghost"
            ml={1}
            onClick={handleEdit}
            color="gray.800"
          />
        </>
      )}
    </Flex>
  );
};
